import React from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  IconButton,
  Button,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Controller } from 'react-hook-form';
import { TimeSlot, DaySchedule, WeeklySchedule } from '../../types/mentor';
import { generateTimeOptions, formatTimeDisplay } from '../../helper/time';

interface WeeklyScheduleFieldProps {
  name: string;
  control: any;
  error?: any;
  label: string;
  required?: boolean;
}

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const defaultTimeSlot: TimeSlot = { startTime: '09:00', endTime: '17:00' };

const defaultSchedule: WeeklySchedule = {
  monday: { enabled: false, timeSlots: [] },
  tuesday: { enabled: false, timeSlots: [] },
  wednesday: { enabled: false, timeSlots: [] },
  thursday: { enabled: false, timeSlots: [] },
  friday: { enabled: false, timeSlots: [] },
  saturday: { enabled: false, timeSlots: [] },
  sunday: { enabled: false, timeSlots: [] },
};

export const WeeklyScheduleField: React.FC<WeeklyScheduleFieldProps> = ({
  name,
  control,
  error,
  label,
  required = false,
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={{ required }}
      defaultValue={defaultSchedule}
      render={({ field: { value, onChange } }) => {
        const schedule: WeeklySchedule = value || defaultSchedule;

        const handleDayToggle = (dayKey: string, enabled: boolean) => {
          const currentDay: DaySchedule = schedule[dayKey as keyof WeeklySchedule];
          const newSchedule = {
            ...schedule,
            [dayKey]: {
              ...currentDay,
              enabled,
              timeSlots: enabled ? 
                (currentDay.timeSlots.length > 0 
                  ? currentDay.timeSlots 
                  : [{ ...defaultTimeSlot }]) 
                : [],
            },
          };
          onChange(newSchedule);
        };

        const handleTimeSlotChange = (dayKey: string, slotIndex: number, field: 'startTime' | 'endTime', value: string) => {
          const daySchedule = schedule[dayKey as keyof WeeklySchedule];
          const newTimeSlots = [...daySchedule.timeSlots];
          newTimeSlots[slotIndex] = {
            ...newTimeSlots[slotIndex],
            [field]: value,
          };

          const newSchedule = {
            ...schedule,
            [dayKey]: {
              ...daySchedule,
              timeSlots: newTimeSlots,
            },
          };
          onChange(newSchedule);
        };

        const addTimeSlot = (dayKey: string) => {
          const daySchedule = schedule[dayKey as keyof WeeklySchedule];
          const newTimeSlots = [...daySchedule.timeSlots, { ...defaultTimeSlot }];

          const newSchedule = {
            ...schedule,
            [dayKey]: {
              ...daySchedule,
              timeSlots: newTimeSlots,
            },
          };
          onChange(newSchedule);
        };

        const removeTimeSlot = (dayKey: string, slotIndex: number) => {
          const daySchedule = schedule[dayKey as keyof WeeklySchedule];
          const newTimeSlots = daySchedule.timeSlots.filter((_, index) => index !== slotIndex);

          const newSchedule = {
            ...schedule,
            [dayKey]: {
              ...daySchedule,
              timeSlots: newTimeSlots,
            },
          };
          onChange(newSchedule);
        };

        const hasError = Boolean(error);
        const helperOrErrorText = error?.type === 'required' ? '此欄位為必填欄位' : '';

        return (
          <Box display="grid" gap={1}>
            <InputLabel
              required={required}
              error={required && hasError}
              sx={{
                fontSize: '18px',
                color: '#141921',
              }}
            >
              {label}
            </InputLabel>

            <Box display="grid" gap={2} sx={{ border: '1px solid #E0E0E0', borderRadius: '8px', padding: '16px' }}>
              {DAYS.map(({ key, label: dayLabel }) => {
                const daySchedule = schedule[key as keyof WeeklySchedule];
                return (
                  <Box key={key} display="grid" gap={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={daySchedule.enabled}
                          onChange={(e) => handleDayToggle(key, e.target.checked)}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
                          {dayLabel}
                        </Typography>
                      }
                    />

                    {daySchedule.enabled && (
                      <Box display="grid" gap={1} sx={{ ml: 4 }}>
                        {daySchedule.timeSlots.map((slot, slotIndex) => {
                          const timeOptions = generateTimeOptions();
                          return (
                            <Box key={slotIndex} display="flex" alignItems="center" gap={1}>
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Start Time</InputLabel>
                                <Select
                                  value={slot.startTime}
                                  onChange={(e) => handleTimeSlotChange(key, slotIndex, 'startTime', e.target.value as string)}
                                  label="Start Time"
                                >
                                  {timeOptions.map((time) => (
                                    <MenuItem key={time} value={time}>
                                      {formatTimeDisplay(time)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              
                              <Typography variant="body2">to</Typography>
                              
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>End Time</InputLabel>
                                <Select
                                  value={slot.endTime}
                                  onChange={(e) => handleTimeSlotChange(key, slotIndex, 'endTime', e.target.value as string)}
                                  label="End Time"
                                >
                                  {timeOptions.map((time) => (
                                    <MenuItem key={time} value={time}>
                                      {formatTimeDisplay(time)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              
                              {daySchedule.timeSlots.length > 1 && (
                                <IconButton
                                  onClick={() => removeTimeSlot(key, slotIndex)}
                                  size="small"
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          );
                        })}
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => addTimeSlot(key)}
                          size="small"
                          variant="outlined"
                          sx={{ width: 'fit-content', mt: 1 }}
                        >
                          Add Time Slot
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {helperOrErrorText && (
              <FormHelperText
                error={hasError}
                sx={{
                  textAlign: 'left',
                  fontSize: '16px',
                  margin: '0px',
                }}
              >
                {helperOrErrorText}
              </FormHelperText>
            )}
          </Box>
        );
      }}
    />
  );
};
